import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { CustomerDetails } from '@/@types/responses/customer/CustomerDetails'
import { Customer } from '@/@types/responses/customer/Customer'
import { CustomerMessage } from '@/@types/messages/CustomerMessage'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class CustomerService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createCustomer(customerMessage: CustomerMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/customers`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse(json.id)
    })
  }

  public createCustomerNote(customerId: number, noteMessage: NoteMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(noteMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/customers/${customerId}/notes`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteCustomer(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/customers/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getCustomer(id: number): Promise<CustomerDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/customers/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new CustomerDetails(json.id, json.firstName, json.lastName, json.phoneNumber, json.email, json.addressLine1, json.addressLine2, json.city, json.stateId, json.postalCode, json.notes, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public searchCustomers(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, ascending: boolean): Promise<PagingResponse<Customer>> {
    const queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/customers${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Customer>
    })
  }

  public updateCustomer(customerId: number, customerMessage: CustomerMessage): Promise<void> {
    const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/customers/${customerId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
